
import { useI18n } from "vue-i18n";
import { resverText } from "@/utils/index";
import { defineComponent, ref, reactive, watch, onMounted, computed, nextTick } from "vue";
import { useRoute } from "vue-router";
import VueQrcode from "@chenfengyuan/vue-qrcode";
import * as Cashier from "@/api/cashier";
import useClipboard from "vue-clipboard3";
import {
  Field,
  Button,
  DropdownMenu,
  DropdownItem,
  Cell,
  CellGroup,
  Toast,
  Loading,
  Overlay,
  Icon,
  Popover,
  ActionSheet,
  Skeleton
} from "vant";

import {
  InterfaceUI,
  CoinMapCountry,
  Languages,
  FormatDateZone,
  CreateOrderParams,
  FormData,
  OptionObject,
  CashPayOutOrderStatus,
  ChannelObject,
  ChannelCurrencyRatesObject,
  SupportedBanksList,
  SupportedCountries
} from "./interface";
import KP_SELECT from "@/components/KP_SELECT.vue";
import KP_INPUT from "@/components/KP_INPUT.vue";
import KP_PICKER from "@/components/KP_PICKER.vue";

export default defineComponent({
  name: "payout",
  components: {
    VueQrcode,
    [Field.name]: Field,
    [Button.name]: Button,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    [Cell.name]: Cell,
    [CellGroup.name]: CellGroup,
    [Toast.name]: Toast,
    [Loading.name]: Loading,
    [Overlay.name]: Overlay,
    [Icon.name]: Icon,
    [Popover.name]: Popover,
    [ActionSheet.name]: ActionSheet,
    [Skeleton.nane]: Skeleton,
    KP_SELECT,
    KP_INPUT,
    KP_PICKER
  },
  setup() {
    const route = useRoute();
    const { t, locale } = useI18n();
    const { lang, k, from } = route.query;

    const showSupplierSheet = ref(false);
    const showBankSheet = ref(false);
    const result = ref(false);
    const showPopover = ref(false);
    const currentLang = ref();

    currentLang.value = lang ?? "en";
    let data = reactive({
      dev: process.env.VUE_APP_BUILD_ENV,
      token: k,
      path: window.location.origin,
      InterfaceUI,
      Languages,
      FormData,
      minData: new Date(),
      Search: {
        supplier: "",
        bank: ""
      }
    });

    const changeLang = item => {
      locale.value = item.value;
    };

    watch(
      currentLang,
      newLang => {
        locale.value = newLang;
      },
      {
        immediate: true
      }
    );
    const getLangName = computed(() => {
      return (
        data.Languages.find(x => {
          return x.value === locale.value;
        })?.text || ""
      );
    });

    // 复制
    const { toClipboard } = useClipboard();
    const copy = async (text: string) => {
      try {
        await toClipboard(text);
        Toast({
          message: t("defray.copied"),
          overlay: false,
          duration: 800
        });
      } catch (e) {
        console.error(e);
      }
    };

    /**
     * 获取通道列表
     */
    const __CashChannelList = ref<Array<ChannelObject>>([]);
    const getChannelList = async () => {
      const loadingToast = Toast({
        message: "",
        type: "loading",
        duration: 0
      });
      const res = await Cashier.CashChannelList();
      loadingToast?.close();
      if (res?.code == 200) {
        let list: [ChannelObject] = res?.data || [];
        __CashChannelList.value = list;
        await getOrderInfo();
        GetChannelListByCoin();
        // 预下单
        if (data.FormData.orderStatus == 0) {
          await getCashierWithdrawRequiredFields();
        }
      } else {
        Toast({
          message: res?.message || t("error"),
          overlay: false,
          duration: 2000
        });
      }
    };

    /**
     * 通过币种筛选出支持的通道列表
     */
    const CashChannelList = ref<Array<ChannelObject>>([]);
    const GetChannelListByCoin = () => {
      let list =
        __CashChannelList.value.filter((x: ChannelObject) => {
          return x.cashChannelCurrencyRateSetting.find((channel: ChannelCurrencyRatesObject) => {
            return channel?.currency === data.FormData.currency && channel?.flowDirection === 1;
          });
        }) || [];
      CashChannelList.value = list;
      if (list.length) {
        data.FormData.channelCode = list[0]?.code;
      }
    };

    /**
     * 通过通道获取对应的支付方式
     */
    const PaymentTypeList = computed(() => {
      if (!data.FormData.channelCode) return;
      let cashChannelCurrencyRateSetting =
        CashChannelList.value.find((x: ChannelObject) => {
          return x.code == data.FormData.channelCode;
        })?.cashChannelCurrencyRateSetting || [];

      let __data =
        cashChannelCurrencyRateSetting.filter(x => {
          return (
            x.currency == data.FormData.currency && x.flowDirection === 1 && x.supportedChannelType
          );
        }) || [];

      let list: Array<ChannelCurrencyRatesObject> = [];
      let temp = {};
      __data.map(x => {
        if (!temp[x.supportedChannelType]) {
          temp[x.supportedChannelType] = x.supportedChannelType;
          list.push(x);
        }
      });

      if (list.length > 0) {
        let item = list[0] || {};
        data.FormData.paymentType = item?.supportedChannelType;
        data.FormData.transactionFee = item?.transactionFee || 0;
      }
      // 处理没有任何渠道类型时，费率问题
      else {
        __data =
          cashChannelCurrencyRateSetting.filter(x => {
            return x.currency == data.FormData.currency && x.flowDirection == 0;
          }) || [];
        if (__data.length) {
          data.FormData.transactionFee = __data[0]?.transactionFee || 0;
        }
      }

      return list;
    });

    /**
     * 通过编码查找对应的名称
     * @param code 编码Code
     * @param list 列表数据
     */
    const getNameByCode = (code: string, list: Array<OptionObject>) => {
      return (
        list.find((x: OptionObject) => {
          return x.value == code;
        })?.key || ""
      );
    };

    // 运营商列表
    const SupplierList = computed(() => {
      let list =
        __CashChannelList.value.find((x: ChannelObject) => {
          return x.code == data.FormData.channelCode;
        })?.cashChannelCurrencyRateSetting || [];

      let temp =
        list.filter(x => {
          return (
            x.currency == data.FormData.currency && x.flowDirection === 1 && x.supportedMobileType
          );
        }) || [];

      if (temp.length > 1) {
        return temp.map(x => {
          return {
            name: x.supportedMobileType,
            value: x.supportedMobileType
          };
        });
      } else if (temp.length == 1) {
        let __supportedMobileType = temp[0].supportedMobileType;
        if (Array.isArray(__supportedMobileType)) {
          return __supportedMobileType.map(x => {
            return {
              name: x,
              value: x
            };
          });
        } else {
          return {
            name: __supportedMobileType,
            value: __supportedMobileType
          };
        }
      }
    });

    let extendFieleds = [];
    const onSelectChange = val => {
      if (val.fieldName.includes("bank")) {
        data.FormData.bankName =
          val.list.find(x => {
            return x.key == val.value.key;
          })?.value || "";
        return;
      }

      const childs =
        val.list.find(x => {
          return x.key == val.value.key;
        })?.childs || [];

      if (!childs.length) {
        return;
      }

      extendFieleds.map(x => {
        // @ts-ignore
        data.FormData[x] = "";
        delete data.FormData[x];
        dynamicsFiledList.value.splice(
          // @ts-ignore
          dynamicsFiledList.value.findIndex(f => f?.key == val.value.key),
          1
        );
      });

      nextTick(() => {
        extendFieleds = [];
        childs.map(x => {
          data.FormData[x?.field_name] = "";
          // @ts-ignore
          dynamicsFiledList.value.push(x);
        });
        extendFieleds = childs.map(x => {
          return x.field_name;
        });
      });
    };

    // 银行列表
    const BankList = computed(() => {
      let list =
        __CashChannelList.value.find((x: ChannelObject) => {
          return x.code == data.FormData.channelCode;
        })?.cashChannelCurrencyRateSetting || [];

      let supportBanks =
        list?.find(x => {
          return (
            x.currency == data.FormData.currency &&
            x.flowDirection === 1 &&
            (["DIRECT", "BANK", "SPEI"].includes(x.supportedChannelType) ||
              (x.supportedChannelType == "BANKTRANSFER" && data.FormData.channelCode == "DukPay"))
          );
        })?.supportBanks || [];

      // 输入筛选过滤
      if (data.Search.bank) {
        supportBanks = supportBanks.filter(x => {
          return (
            x.bankCode.includes(data.Search.bank) ||
            x.bankName.toLocaleLowerCase().includes(data.Search.bank.toLocaleLowerCase())
          );
        });
      }

      return supportBanks.map((x: SupportedBanksList) => {
        return {
          name: x.bankName,
          value: x.bankCode
        };
      });
    });
    const onSelectBank = val => {
      showBankSheet.value = false;
      data.FormData.bankCode = val.value;
    };

    // 国家列表
    const CountryList = computed(() => {
      let list =
        __CashChannelList.value.find((x: ChannelObject) => {
          return x.code == data.FormData.currency;
        })?.cashChannelCurrencyRateSetting || [];

      let temp =
        list.find(x => {
          return (
            x.currency == data.FormData.currency &&
            x.flowDirection === 1 &&
            ["MOMO", "OVO"].includes(x.supportedChannelType)
          );
        })?.supportedCountries || [];

      return temp.map((x: SupportedCountries) => {
        return {
          name: `${x.name} (${x.code})`,
          value: x.code
        };
      });
    });

    /**
     * 获取订单信息
     */
    const getOrderInfo = async () => {
      const loadingToast = Toast({
        message: "",
        type: "loading",
        duration: 0
      });
      const res = await Cashier.GetPayOutOrderInfo();
      loadingToast?.close();
      if (res?.code == 200) {
        data.FormData = Object.assign(data.FormData, res?.data || {});
        if (data.FormData.orderStatus == 1) {
          setTimeout(() => {
            getStatus();
          }, 4000);
        }
      } else {
        Toast({
          message: res?.message || t("error"),
          overlay: false,
          duration: 2000
        });
      }
    };

    // 轮询获取状态
    const getStatus = async () => {
      const res = await Cashier.GetPayOutOrderInfo();
      if (res?.code == 200) {
        data.FormData = Object.assign(data.FormData, res?.data || {});
        switch (data.FormData.orderStatus) {
          // Pending
          case 1:
          case 2:
          case 3:
          case 4:
            setTimeout(() => {
              getStatus();
            }, 10000);
            break;
          // Success
          case 5:
            setTimeout(() => {
              paymentUrl(data.FormData.redirectUrl, "SUCCESS");
            }, 1000);
            break;
          // Fail
          case 6:
          case 7:
            paymentUrl(data.FormData.cancelUrl, "FAIL");
            break;
          // Cancel
          case 8:
            paymentUrl(data.FormData.cancelUrl, "CANCEL");
            break;
        }
      } else {
        setTimeout(() => {
          getStatus();
        }, 5000);
      }
    };

    /**
     * 付款路由操作行为
     **/
    const paymentUrl = (url, type) => {
      if (from == "app") {
        const status = {
          SUCCESS: "CASHIER-TOPUP-SUCCESS",
          FAIL: "CASHIER-TOPUP-FAIL",
          CANCEL: "CASHIER-TOPUP-CANCEL"
        };
        window["BacktrackToApp"]?.postMessage(
          JSON.stringify({
            key: status[type],
            data: null
          })
        );
        return;
      }
      url && (window.location.href = url);
    };

    /**
     * 上一步、下一步
     */
    const step = ref(1);
    const steps = ref(1);
    const nextStep = (val: boolean) => {
      if (val) {
        if (step.value == steps.value) {
          CreateWithdrawOrder();
        } else {
          step.value = step.value + 1;
        }
      } else {
        step.value = step.value - 1;
      }
    };

    // 下一步按钮禁用逻辑
    const isDisableConfirm = computed(() => {
      let result = false;
      dynamicsFiledList.value.map(x => {
        // @ts-ignore
        if (!data.FormData[x?.field_name]) {
          result = true;
        }
      });
      return result;
    });

    /**
     * 发起提现申请
     */
    const CreateWithdrawOrder = async () => {
      if (isDisableConfirm.value) return;
      let param: any = {};
      if (data.FormData.bankName) {
        param.bank_name = data.FormData.bankName;
      }
      dynamicsFiledList.value.map(x => {
        // @ts-ignore
        const value = data.FormData[x?.field_name].trim();
        // @ts-ignore
        param[x?.field_name] = `${x?.expand ? x?.expand : ""}${value}`;
      });
      const loadingToast = Toast({
        message: "",
        type: "loading",
        overlay: true,
        duration: 0
      });
      const res = await Cashier.CreatePayOut(param);
      loadingToast.close();
      if (res?.code == 200) {
        data.FormData.extend = res?.data || {};
        Toast({
          message: res?.message || t("cashier.pending"),
          overlay: false,
          duration: 4000
        });
        getOrderInfo();
      } else {
        Toast({
          message: res?.message || t("cashier.error"),
          overlay: false,
          duration: 4000
        });
      }
    };

    const dynamicsFiledList = ref([]);
    const getCashierWithdrawRequiredFields = async () => {
      const loadingToast = Toast({
        message: "",
        type: "loading",
        overlay: true,
        duration: 0
      });
      const res = await Cashier.CashierWithdrawRequiredFields();
      loadingToast.close();
      if (res?.code == 200) {
        const temp = res?.data || [];
        const list = temp.filter(x => !["RedirectUrl", "UserIp"].includes(x.field_name));
        // Pre-sort "Supplier" and "BankCode" field
        list.unshift(
          ...list.splice(
            list.findIndex(item => ["bank_code", "supplier"].includes(item.field_name)),
            1
          )
        );
        dynamicsFiledList.value = list;
        dynamicsFiledList.value.map(x => {
          // @ts-ignore
          data.FormData[x?.field_name] = "";
        });
        steps.value = dynamicsFiledList.value.length ? 2 : 1;
      }
    };

    onMounted(() => {
      if (k == undefined || !k) {
        Toast({
          message: t("error"),
          overlay: false,
          duration: 4000
        });
        return;
      }
      getChannelList();
    });

    return {
      locale,
      resverText,
      data,
      currentLang,
      showPopover,
      showSupplierSheet,
      showBankSheet,
      changeLang,
      getLangName,
      FormatDateZone,
      copy,

      paymentUrl,
      isDisableConfirm,
      step,
      steps,
      nextStep,
      result,

      CashPayOutOrderStatus,
      CashChannelList,
      PaymentTypeList,
      getNameByCode,

      SupplierList,
      onSelectChange,
      BankList,
      onSelectBank,

      dynamicsFiledList
    };
  }
});
